/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { Spin } from "antd";
import { useTranslation, Trans } from "react-i18next";
import {
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  QrCodeIcon,
  PhoneIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/solid";
import { ALGERIAN_SHIPPING_COMPANIES } from "../../../constants/algerian_shipping_companies";

const STEPS = [1, 2, 3, 4, 5];
const APP_ICON = "/phone-call.png";
const USER_API_KEY = process.env.REACT_APP_USER_API_KEY;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ActivationSmartSuivi({ close }) {
  const account = useSelector((state) => state.AccountReducer);
  const store = useSelector((state) => state.StoreReducer);
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  // generate phone api key
  const [apiKey, setApiKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(false);
  const [keyError, setKeyError] = useState(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);

  // get phone numbers
  const [detectedPhones, setDetectedPhones] = useState([]);
  const [countdown, setCountdown] = useState(15);

  // select delivery company
  const [selectedCompanyKey, setSelectedCompanyKey] = useState(null);
  const [linkedCompanies, setLinkedCompanies] = useState([]);

  // cleanup timer on unmount or step change
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // check if we already have smartsuivi key
  useEffect(() => {
    const checkExistingKey = async () => {
      setIsCheckingExisting(true);
      setApiKey(null);
      if (!store?.id) {
        setIsCheckingExisting(false);
        return;
      }

      try {
        const storeSnap = await getDoc(doc(db, "store", store.id));
        const existing = storeSnap.exists()
          ? storeSnap.data().smartSuivi
          : null;

        if (existing?.apiKey) {
          setApiKey(existing.apiKey);

          if (storeSnap.data()?.smartSuiviActive) {
            setCurrentStep(5);
          } else if (
            Array.isArray(existing.phones) &&
            existing.phones.length > 0
          ) {
            setDetectedPhones(existing.phones);
            setCurrentStep(4);
          } else {
            setCurrentStep(1);
          }
        }
      } catch (err) {
        console.error("[smartSuivi] error checking existing key", err);
      } finally {
        setIsCheckingExisting(false);
      }
    };

    checkExistingKey();
  }, [store?.id]);

  // poll every 5 seconds while on step 2 to detect if the user scanned the QR
  useEffect(() => {
    if (currentStep !== 2 || !apiKey || keyLoading || keyError) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/phone-api-keys?limit=50`, {
          headers: { "x-api-key": USER_API_KEY },
        });

        const body = await res.json().catch(() => ({}));

        if (!res.ok || body?.status !== "success") return;

        const ourKey = (body.data || []).find((k) => k.api_key === apiKey);

        if (
          ourKey &&
          Array.isArray(ourKey.phone_numbers) &&
          ourKey.phone_numbers.length > 0
        ) {
          const phones = ourKey.phone_numbers.slice(0, 2);

          setDetectedPhones(phones);
          clearInterval(interval);
          if (store?.id) {
            try {
              await setDoc(
                doc(db, "store", store.id),
                {
                  smartSuivi: {
                    phones,
                    updatedAt: serverTimestamp(),
                  },
                },
                { merge: true },
              );
            } catch (err) {
              console.error("[smartSuivi] failed to save phones", err);
            }
          }

          setCurrentStep(3);
        }
      } catch (err) {
        console.error("[smartSuivi] polling error", err);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [currentStep, apiKey, keyLoading, keyError, store?.id]);

  useEffect(() => {
    if (currentStep !== 3) return;

    setCountdown(15);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCurrentStep(4);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentStep]);

  useEffect(() => {
    if (!store) return;

    const companies = ALGERIAN_SHIPPING_COMPANIES.filter(
      (c) => c.key !== "other" && c.check(store),
    ).map((c) => ({
      ...c,
      name: t(`services.foorCall.companies.${c.nameKey}`, {
        defaultValue: c.nameKey,
      }),
    }));

    setLinkedCompanies(companies);
  }, [store, t]);

  const generatePhoneApiKey = async () => {
    if (apiKey) return;
    setKeyLoading(true);
    setKeyError(null);
    setApiKey(null);

    try {
      const res = await fetch(`${API_BASE_URL}/phone-api-keys`, {
        method: "POST",
        headers: {
          "x-api-key": USER_API_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: `phone api key of ${account?.email}`,
        }),
      });

      const body = await res.json().catch(() => ({}));

      if (res.ok && body?.status === "success" && body?.data?.api_key) {
        setApiKey(body.data.api_key);
        if (store?.id) {
          try {
            await setDoc(
              doc(db, "store", store.id),
              {
                smartSuivi: {
                  apiKey: body.data.api_key,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  phones: [],
                },
              },
              { merge: true },
            );
          } catch (firebaseErr) {
            console.error("[smartSuivi] failed to save to store", firebaseErr);
          }
        }
        return;
      }

      console.error("[generatePhoneApiKey] failed", {
        status: res.status,
        body,
      });

      setKeyError("we're facing a problem, please try later");
    } catch (err) {
      console.error("[generatePhoneApiKey] exception", err);
      setKeyError("we're facing a problem, please try later");
    } finally {
      setKeyLoading(false);
    }
  };

  const handleInstalled = () => {
    goToStep(2);
    generatePhoneApiKey();
  };

  const handlePhonesIncorrect = () => {
    goToStep(2);
    generatePhoneApiKey();
  };

  const goToStep = (step) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setLoading(false);
    setCurrentStep(step);
  };

  const handlePhonesCorrect = () => {
    goToStep(4);
  };

  // step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-3 mb-8">
      {STEPS.map((step, index) => {
        const isCompleted = currentStep > step;
        const isActive = currentStep === step;

        return (
          <React.Fragment key={step}>
            <div
              className={`
                w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                transition-all duration-300 border-2
                ${
                  isCompleted
                    ? "bg-[#9b59bf] border-[#9b59bf] text-white"
                    : isActive
                      ? "bg-white dark:!bg-[#2a2a2a] border-[#9b59bf] text-[#9b59bf]"
                      : "bg-white dark:!bg-[#2a2a2a] border-gray-300 dark:!border-[#3c3a3d] text-gray-400"
                }
              `}
            >
              {isCompleted ? <CheckCircleIcon className="w-5 h-5" /> : step}
            </div>

            {index < STEPS.length - 1 && (
              <div
                className={`
                  h-0.5 w-8 sm:w-12 transition-colors duration-300
                  ${
                    currentStep > step
                      ? "bg-[#9b59bf]"
                      : "bg-gray-200 dark:!bg-[#3c3a3d]"
                  }
                `}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // step 1: install app
  const Step1 = () => (
    <div className="flex flex-col items-center text-center px-4 py-6">
      <div className="w-20 h-20 rounded-2xl border border-[#fbf4ff] dark:!border-[#3c3a3d] bg-white dark:!bg-[#2a2a2a] flex items-center justify-center mb-5 shadow-sm">
        <img
          src={APP_ICON}
          alt={t("services.smartsuivi.appName")}
          className="w-12 h-12 object-contain"
        />
      </div>

      <h3 className="text-xl font-bold text-gray-800 dark:!text-gray-100 mb-2">
        {t("services.smartsuivi.step1.title", {
          appName: t("services.smartsuivi.appName"),
        })}
      </h3>
      <p className="text-gray-600 dark:!text-gray-400 max-w-md mb-8 leading-relaxed">
        <Trans
          i18nKey="services.smartsuivi.step1.description"
          values={{ appName: t("services.smartsuivi.appName") }}
          components={{ strong: <strong /> }}
        />
      </p>

      <button
        onClick={handleInstalled}
        className="bg-[#9b59bf] hover:bg-[#814a9f] text-white font-medium py-2.5 px-8 rounded-lg transition-colors duration-200 flex items-center gap-2"
      >
        <DevicePhoneMobileIcon className="w-5 h-5" />
        {t("services.smartsuivi.step1.button")}
      </button>
    </div>
  );

  // step 2: scan qr code
  const Step2 = () => (
    <Spin spinning={loading}>
      <div className="flex flex-col items-center text-center px-4 py-6">
        <div className="flex items-center gap-2 mb-4">
          <QrCodeIcon className="w-6 h-6 text-[#9b59bf]" />
          <h3 className="text-xl font-bold text-gray-800 dark:!text-gray-100">
            {t("services.smartsuivi.step2.title")}
          </h3>
        </div>

        <p className="text-gray-600 dark:!text-gray-400 max-w-md mb-6 leading-relaxed">
          <Trans
            i18nKey="services.smartsuivi.step2.description"
            values={{ appName: t("services.smartsuivi.appName") }}
            components={{ strong: <strong /> }}
          />
        </p>

        <div
          className="w-full max-w-md flex flex-col items-center mb-6"
          style={{ minHeight: 320 }}
        >
          {keyError ? (
            <div className="flex-1 flex items-center justify-center w-full">
              <p className="text-red-500 dark:!text-red-400 text-center text-base font-medium leading-relaxed px-4">
                {keyError}
              </p>
            </div>
          ) : (
            <>
              <div className="relative bg-white p-4 rounded-xl border border-[#fbf4ff] dark:!border-[#3c3a3d] shadow-sm mb-5">
                {apiKey ? (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(apiKey)}`}
                    alt={t("ui.smartSuiviQr")}
                    className="w-48 h-48"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-100 dark:!bg-[#2a2a2a] rounded-lg blur-sm" />
                )}

                {keyLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-black/40 rounded-xl">
                    <Spin size="large" />
                  </div>
                )}
              </div>

              <div className="w-full">
                <label className="block text-sm font-medium text-gray-500 dark:!text-gray-400 mb-1 text-left">
                  {t("services.smartsuivi.step2.apiKeyLabel")}
                </label>

                <div className="flex items-center gap-2 bg-[#fcf6ff] dark:!bg-[#222222] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-lg px-3 py-2.5 min-h-[42px]">
                  {keyLoading ? (
                    <div className="flex-1 flex items-center justify-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9b59bf] animate-bounce [animation-delay:-0.3s]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9b59bf] animate-bounce [animation-delay:-0.15s]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9b59bf] animate-bounce" />
                    </div>
                  ) : apiKey && (
                    <>
                      <code className="flex-1 text-sm text-gray-800 dark:!text-gray-200 font-mono truncate">
                        {apiKey}
                      </code>
                      <button
                        onClick={() => navigator.clipboard.writeText(apiKey)}
                        className="text-[#9b59bf] hover:text-[#814a9f] text-sm font-medium whitespace-nowrap"
                      >
                        {t("services.smartsuivi.step2.copy")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Spin>
  );

  // step 3: verify phone numbers
  const Step3 = () => (
    <div className="flex flex-col items-center text-center px-4 py-6">
      <div className="flex items-center gap-2 mb-4">
        <PhoneIcon className="w-6 h-6 text-[#9b59bf]" />
        <h3 className="text-xl font-bold text-gray-800 dark:!text-gray-100">
          {t("services.smartsuivi.step3.title")}
        </h3>
      </div>

      <p className="text-gray-600 dark:!text-gray-400 max-w-md mb-8 leading-relaxed">
        {t("services.smartsuivi.step3.description")}
      </p>

      <div className="w-full max-w-sm space-y-3 mb-8">
        {detectedPhones.map((phone, index) => (
          <div
            key={phone}
            className="bg-[#fcf6ff] dark:!bg-[#222222] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl px-5 py-4"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-500 dark:!text-gray-400 shrink-0">
                {t(`services.smartsuivi.step3.number${index + 1}`, {
                  number: index + 1,
                })}
              </span>

              <span
                dir="ltr"
                className="font-semibold text-gray-800 dark:!text-gray-100 tracking-wide text-right truncate"
              >
                {phone}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="relative w-20 h-20 flex items-center justify-center mt-2">
        <svg
          className="absolute inset-0 w-full h-full -rotate-90"
          viewBox="0 0 36 36"
        >
          <path
            className="text-gray-200 dark:text-[#3c3a3d]"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          />

          <path
            className="text-[#9b59bf]"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="100"
            strokeDashoffset={100 - (countdown / 15) * 100}
            style={{ transition: "stroke-dashoffset 1s linear" }}
          />
        </svg>

        <span className="text-2xl font-bold text-[#9b59bf] tabular-nums">
          {countdown}
        </span>
      </div>
    </div>
  );

  // step 4: select delivery company
  const Step4 = () => (
    <div className="flex flex-col items-center text-center px-4 py-6">
      <div className="flex items-center gap-2 mb-4">
        <PhoneIcon className="w-6 h-6 text-[#9b59bf]" />
        <h3 className="text-xl font-bold text-gray-800 dark:!text-gray-100">
          {t("services.smartsuivi.step4.title", "Select delivery company")}
        </h3>
      </div>

      <p className="text-gray-600 dark:!text-gray-400 max-w-md mb-8 leading-relaxed">
        {t(
          "services.smartsuivi.step4.description",
          "Choose one of your connected delivery companies to continue.",
        )}
      </p>

      {linkedCompanies.length === 0 ? (
        <div className="w-full max-w-sm bg-amber-50 dark:!bg-amber-900/20 border border-amber-200 dark:!border-amber-700/40 rounded-xl px-5 py-6 text-center">
          <p className="text-amber-700 dark:!text-amber-300 text-sm font-medium leading-relaxed">
            {t(
              "services.smartsuivi.step4.noCompany",
              "Please activate at least one delivery company first.",
            )}
          </p>
        </div>
      ) : (
        <>
          <div className="w-full max-w-sm grid grid-cols-2 gap-3 mb-8">
            {linkedCompanies.map((company) => {
              const isSelected = selectedCompanyKey === company.key;
              return (
                <button
                  key={company.key}
                  onClick={() => setSelectedCompanyKey(company.key)}
                  className={`
                  flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all
                  ${
                    isSelected
                      ? "border-[#9b59bf] bg-[#fbf4ff] dark:!bg-[#2a1f33]"
                      : "border-gray-200 dark:!border-[#3c3a3d] bg-white dark:!bg-[#222222] hover:border-[#9b59bf]/50"
                  }
                `}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#fcf6ff] dark:!bg-[#181819] flex items-center justify-center overflow-hidden">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={company.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <PhoneIcon className="w-5 h-5 text-[#9b59bf]" />
                    )}
                  </div>
                  <span className="text-xs font-semibold text-gray-700 dark:!text-gray-200 text-center leading-tight">
                    {company.name}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            disabled={!selectedCompanyKey}
            onClick={async () => {
              if (store?.id) {
                try {
                  await setDoc(
                    doc(db, "store", store.id),
                    {
                      smartSuiviActive: true,
                      smartSuivi: {
                        selectedCompanyKey,
                        updatedAt: serverTimestamp(),
                      },
                    },
                    { merge: true },
                  );
                } catch (err) {
                  console.error("[smartSuivi] failed to activate", err);
                }
              }
              setCurrentStep(5);
            }}
            className={`
            w-full max-w-sm py-2.5 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2
            ${
              selectedCompanyKey
                ? "bg-[#9b59bf] hover:bg-[#814a9f] text-white"
                : "bg-gray-200 dark:!bg-[#3c3a3d] text-gray-400 cursor-not-allowed"
            }
          `}
          >
            <CheckCircleIcon className="w-5 h-5" />
            {t("services.smartsuivi.step4.activate")}
          </button>
        </>
      )}
    </div>
  );

  const Step5 = () => (
    <div className="flex flex-col items-center text-center px-4 py-10">
      <div className="w-16 h-16 rounded-full bg-green-100 dark:!bg-green-900/30 flex items-center justify-center mb-5">
        <CheckCircleIcon className="w-9 h-9 text-green-600 dark:!text-green-400" />
      </div>
      <h3 className="text-xl font-bold text-gray-800 dark:!text-gray-100 mb-2">
        {t("services.smartsuivi.step5.title", "Service activated")}
      </h3>
      <p className="text-gray-600 dark:!text-gray-400 max-w-md">
        {t(
          "services.smartsuivi.step5.description",
          "Smart Suivi is now active on your store.",
        )}
      </p>
    </div>
  );

  return (
    <div className="min-h-[480px] dark:!bg-[#181819]">
      <div className="px-4 pt-6 pb-2">
        <StepIndicator />
      </div>

      <div className="px-2 pb-6">
        {isCheckingExisting ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {currentStep === 1 && <Step1 />}
            {currentStep === 2 && <Step2 />}
            {currentStep === 3 && <Step3 />}
            {currentStep === 4 && <Step4 />}
            {currentStep === 5 && <Step5 />}
          </>
        )}
      </div>
    </div>
  );
}
